import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../../components/admin/StatusBadge';

type FilterStatus = 'all' | 'draft' | 'sent' | 'paid' | 'overdue';

interface SendModalState {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber: string;
  buyerName: string;
  buyerEmail: string;
  total: string;
  currency: string;
}

const initialSendModal: SendModalState = {
  isOpen: false,
  invoiceId: '',
  invoiceNumber: '',
  buyerName: '',
  buyerEmail: '',
  total: '',
  currency: 'USD',
};

export const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterStatus>('all');
  const navigate = useNavigate();

  const [sendModal, setSendModal] = useState<SendModalState>(initialSendModal);
  const [sendEmail, setSendEmail] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendFiles, setSendFiles] = useState<{ filename: string; content: string; contentType: string }[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch('/api/admin/invoices', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setInvoices(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const getDisplayStatus = (inv: any) => {
    if (inv.status === 'sent' && inv.dueDate && new Date(inv.dueDate) < new Date()) return 'overdue';
    return inv.status;
  };

  const stats = useMemo(() => {
    let totalOutstanding = 0;
    let totalOverdue = 0;
    let totalPaidThisMonth = 0;
    let draftCount = 0;
    let sentCount = 0;
    let overdueCount = 0;
    let paidCount = 0;

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    invoices.forEach(inv => {
      const status = getDisplayStatus(inv);
      const amount = parseFloat(inv.total) || 0;

      if (status === 'draft') draftCount++;
      else if (status === 'sent') { sentCount++; totalOutstanding += amount; }
      else if (status === 'overdue') { overdueCount++; totalOverdue += amount; totalOutstanding += amount; }
      else if (status === 'paid') {
        paidCount++;
        if (inv.paidAt && new Date(inv.paidAt) >= monthStart) totalPaidThisMonth += amount;
      }
    });

    return { totalOutstanding, totalOverdue, totalPaidThisMonth, draftCount, sentCount, overdueCount, paidCount };
  }, [invoices]);

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      const status = getDisplayStatus(inv);
      if (filter !== 'all' && status !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        const matchesSearch =
          inv.invoiceNumber?.toLowerCase().includes(q) ||
          inv.buyerName?.toLowerCase().includes(q) ||
          inv.buyerCompany?.toLowerCase().includes(q) ||
          inv.contractReference?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      return true;
    });
  }, [invoices, filter, search]);

  const handleDelete = async (id: string, number: string) => {
    if (!confirm(`Delete invoice ${number}?`)) return;
    await fetch('/api/admin/invoices', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    setInvoices(prev => prev.filter(i => i.id !== id));
  };

  const handleMarkPaid = async (id: string) => {
    const res = await fetch('/api/admin/invoices', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id, status: 'paid' }),
    });
    if (res.ok) {
      setInvoices(prev => prev.map(i => i.id === id ? { ...i, status: 'paid', paidAt: new Date().toISOString() } : i));
    }
  };

  const openSendModal = (inv: any) => {
    setSendModal({
      isOpen: true,
      invoiceId: inv.id,
      invoiceNumber: inv.invoiceNumber,
      buyerName: inv.buyerCompany || inv.buyerName,
      buyerEmail: inv.buyerEmail || '',
      total: inv.total,
      currency: inv.currency,
    });
    setSendEmail(inv.buyerEmail || '');
    setSendCc('');
    setSendFiles([]);
    setSendError('');
    setSending(false);
  };

  const closeSendModal = () => {
    setSendModal(initialSendModal);
    setSendCc('');
    setSendFiles([]);
    setSendError('');
  };

  const handleFileAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      if (file.size > 8 * 1024 * 1024) {
        setSendError(`File "${file.name}" is too large (max 8 MB).`);
        continue;
      }
      const buffer = await file.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
      setSendFiles(prev => [...prev, {
        filename: file.name,
        content: base64,
        contentType: file.type || 'application/pdf',
      }]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setSendFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    const emailList = sendEmail.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    if (!emailList.length) {
      setSendError('Recipient email is required.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailList.some(e => !emailRe.test(e))) {
      setSendError('Please enter valid email addresses, separated by commas.');
      return;
    }

    setSending(true);
    setSendError('');

    try {
      const res = await fetch(`/api/invoices/${sendModal.invoiceId}?action=send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: emailList.join(','),
          cc: sendCc.trim() || undefined,
          extraAttachments: sendFiles.length > 0 ? sendFiles : undefined,
        }),
      });

      if (res.ok) {
        setInvoices(prev => prev.map(i =>
          i.id === sendModal.invoiceId
            ? { ...i, status: 'sent', issuedAt: i.issuedAt || new Date().toISOString(), buyerEmail: emailList[0] }
            : i
        ));
        closeSendModal();
      } else {
        const data = await res.json().catch(() => ({}));
        setSendError(data.error || 'Failed to send invoice. Please try again.');
      }
    } catch {
      setSendError('Network error. Please check your connection.');
    } finally {
      setSending(false);
    }
  };

  const handleDuplicate = async (inv: any) => {
    navigate('/admin/invoices/new', {
      state: {
        duplicate: {
          buyerName: inv.buyerName,
          buyerEmail: inv.buyerEmail,
          buyerCompany: inv.buyerCompany,
          buyerAddress: inv.buyerAddress,
          contractReference: inv.contractReference,
          currency: inv.currency,
          paymentTerms: inv.paymentTerms,
          taxRate: inv.taxRate,
          notes: inv.notes,
        },
      },
    });
  };

  const fmtCurrency = (amount: number, currency: string = 'USD') => {
    const symbols: Record<string, string> = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KES ' };
    const sym = symbols[currency] || currency + ' ';
    return `${sym}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const filterTabs: { label: string; value: FilterStatus; count: number }[] = [
    { label: 'All', value: 'all', count: invoices.length },
    { label: 'Draft', value: 'draft', count: stats.draftCount },
    { label: 'Sent', value: 'sent', count: stats.sentCount },
    { label: 'Overdue', value: 'overdue', count: stats.overdueCount },
    { label: 'Paid', value: 'paid', count: stats.paidCount },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Invoices</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{invoices.length} total invoices</p>
        </div>
        <Link
          to="/admin/invoices/new"
          className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10"
        >
          + New Invoice
        </Link>
      </div>

      {/* Stats cards */}
      {!loading && invoices.length > 0 && (
        <div className="grid grid-cols-3 gap-5 mb-8">
          <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
            <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold">Outstanding</p>
            <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">
              {fmtCurrency(stats.totalOutstanding)}
            </p>
            <p className="text-[13px] text-[#A3A3A3] mt-1">{stats.sentCount + stats.overdueCount} unpaid invoices</p>
          </div>
          <div className={`bg-white border rounded-2xl p-5 shadow-sm shadow-black/[0.03] ${stats.overdueCount > 0 ? 'border-red-200/80' : 'border-[#E8E6E2]/60'}`}>
            <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold">Overdue</p>
            <p className={`text-2xl font-bold mt-1.5 tabular-nums ${stats.overdueCount > 0 ? 'text-red-600' : 'text-[#1A1A1A]'}`}>
              {fmtCurrency(stats.totalOverdue)}
            </p>
            <p className="text-[13px] text-[#A3A3A3] mt-1">{stats.overdueCount} overdue invoices</p>
          </div>
          <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
            <p className="text-[11px] uppercase tracking-[0.12em] text-[#A3A3A3] font-semibold">Paid This Month</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1.5 tabular-nums">
              {fmtCurrency(stats.totalPaidThisMonth)}
            </p>
            <p className="text-[13px] text-[#A3A3A3] mt-1">{stats.paidCount} paid total</p>
          </div>
        </div>
      )}

      {/* Filter tabs + search */}
      <div className="flex items-center justify-between mb-5 gap-4">
        <div className="flex gap-1 bg-[#F5F3F0] rounded-xl p-1">
          {filterTabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${
                filter === tab.value
                  ? 'bg-white text-[#1A1A1A] shadow-sm'
                  : 'text-[#A3A3A3] hover:text-[#6B6B6B]'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1.5 ${filter === tab.value ? 'text-[#6B6B6B]' : 'text-[#C4C4C4]'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#A3A3A3" strokeWidth="1.5" className="absolute left-3.5 top-1/2 -translate-y-1/2">
            <circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search invoices..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-64 pl-10 pr-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm bg-[#FAFAF8] text-[#1A1A1A] placeholder-[#C4C4C4] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-white rounded-2xl animate-pulse shadow-sm" />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Invoice</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Client</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Amount</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Due</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const status = getDisplayStatus(inv);
                return (
                  <tr key={inv.id} className="border-b border-[#E8E6E2]/60 last:border-0 hover:bg-[#FAFAF8]/50 transition-colors">
                    <td className="px-5 py-4">
                      <span className="font-semibold text-[#1A1A1A]">{inv.invoiceNumber}</span>
                      {inv.senderEntity === 'alpine' && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-50 text-emerald-700 rounded-md">ALPINE</span>
                      )}
                      {inv.contractReference && (
                        <span className="block text-[11px] text-[#A3A3A3] mt-0.5">Ref: {inv.contractReference}</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-[#1A1A1A]">{inv.buyerCompany || inv.buyerName}</span>
                      {inv.buyerCompany && inv.buyerCompany !== inv.buyerName && (
                        <span className="block text-[11px] text-[#A3A3A3] mt-0.5">{inv.buyerName}</span>
                      )}
                    </td>
                    <td className="px-5 py-4 font-semibold text-[#1A1A1A] tabular-nums">
                      {fmtCurrency(parseFloat(inv.total), inv.currency)}
                    </td>
                    <td className="px-5 py-4 text-[#6B6B6B] tabular-nums">
                      {inv.dueDate ? new Date(inv.dueDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014'}
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={status} /></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={`/api/invoices/${inv.id}?action=pdf`}
                          target="_blank"
                          rel="noopener"
                          className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors"
                          title="View PDF"
                        >
                          PDF
                        </a>
                        {inv.status === 'draft' && (
                          <Link
                            to={`/admin/invoices/${inv.id}`}
                            className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors"
                          >
                            Edit
                          </Link>
                        )}
                        <button
                          onClick={() => openSendModal(inv)}
                          className="px-2.5 py-1.5 text-[12px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          {inv.status === 'draft' ? 'Send' : 'Resend'}
                        </button>
                        {(inv.status === 'sent' || status === 'overdue') && (
                          <button
                            onClick={() => handleMarkPaid(inv.id)}
                            className="px-2.5 py-1.5 text-[12px] font-medium text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors"
                          >
                            Mark Paid
                          </button>
                        )}
                        <button
                          onClick={() => handleDuplicate(inv)}
                          className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors"
                          title="Duplicate"
                        >
                          Copy
                        </button>
                        {inv.status === 'draft' && (
                          <button
                            onClick={() => handleDelete(inv.id, inv.invoiceNumber)}
                            className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-12 text-center">
              <p className="text-[#A3A3A3]">
                {search || filter !== 'all' ? 'No invoices match your filters.' : 'No invoices yet. Create your first one.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Send Invoice Modal */}
      {sendModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeSendModal} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
            {/* Header */}
            <div className="px-6 py-5 border-b border-[#E8E6E2]/60">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A1A]">Send Invoice</h2>
                  <p className="text-sm text-[#A3A3A3] mt-0.5">{sendModal.invoiceNumber}</p>
                </div>
                <button
                  onClick={closeSendModal}
                  className="p-2 text-[#A3A3A3] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-xl transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              <div className="flex items-center justify-between p-4 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/60">
                <div>
                  <p className="text-sm font-semibold text-[#1A1A1A]">{sendModal.buyerName}</p>
                  <p className="text-xs text-[#A3A3A3] mt-0.5">{sendModal.invoiceNumber}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-lg font-bold text-[#1A1A1A] tabular-nums">
                    {fmtCurrency(parseFloat(sendModal.total), sendModal.currency)}
                  </p>
                  <a
                    href={`/api/invoices/${sendModal.invoiceId}?action=pdf`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                    title="Preview PDF before sending"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 6s1.5-4 5-4 5 4 5 4-1.5 4-5 4-5-4-5-4z" stroke="currentColor" strokeWidth="1.1"/><circle cx="6" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.1"/></svg>
                    PDF
                  </a>
                  <a
                    href={`/api/invoices/${sendModal.invoiceId}?action=email-preview`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                    title="Preview the email body before sending"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 2.5h9v7h-9v-7z" stroke="currentColor" strokeWidth="1.1"/><path d="M1.5 3l4.5 3.5L10.5 3" stroke="currentColor" strokeWidth="1.1"/></svg>
                    Email
                  </a>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">
                  Recipient Email(s)
                </label>
                <input
                  type="text"
                  value={sendEmail}
                  onChange={e => { setSendEmail(e.target.value); setSendError(''); }}
                  placeholder="buyer@company.com, another@company.com"
                  className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#1A1A1A] placeholder-[#C4C4C4] bg-[#FAFAF8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
                  autoFocus
                />
                <p className="text-xs text-[#A3A3A3] mt-1.5">Separate multiple addresses with commas.</p>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">
                  CC (optional)
                </label>
                <input
                  type="text"
                  value={sendCc}
                  onChange={e => { setSendCc(e.target.value); setSendError(''); }}
                  placeholder="yourname@example.com, another@example.com"
                  className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#1A1A1A] placeholder-[#C4C4C4] bg-[#FAFAF8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">
                  Attachments
                </label>

                <div className="flex items-center gap-3 p-3 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/60 mb-2">
                  <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 1h5.5L13 4.5V13a2 2 0 01-2 2H5a2 2 0 01-2-2V3a2 2 0 012-2z" stroke="#DC2626" strokeWidth="1.2"/><path d="M9 1v4h4" stroke="#DC2626" strokeWidth="1.2"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{sendModal.invoiceNumber}.pdf</p>
                    <p className="text-[10px] text-[#A3A3A3]">Invoice PDF (auto-generated)</p>
                  </div>
                  <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-semibold">Included</span>
                </div>

                {sendFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-[#FAFAF8] rounded-xl border border-[#E8E6E2]/60 mb-2">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 1h5.5L13 4.5V13a2 2 0 01-2 2H5a2 2 0 01-2-2V3a2 2 0 012-2z" stroke="#2563EB" strokeWidth="1.2"/><path d="M9 1v4h4" stroke="#2563EB" strokeWidth="1.2"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A1A1A] truncate">{file.filename}</p>
                    </div>
                    <button
                      onClick={() => removeFile(idx)}
                      className="text-[#C4C4C4] hover:text-red-500 transition-colors p-1.5 hover:bg-red-50 rounded-lg"
                      title="Remove"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10.5 3.5l-7 7M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                ))}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileAdd}
                  multiple
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full mt-1 px-4 py-2.5 border border-dashed border-[#D4D0CB] rounded-xl text-sm text-[#6B6B6B] hover:text-[#1A1A1A] hover:border-[#1A1A1A]/30 hover:bg-[#FAFAF8] transition-all"
                >
                  + Attach signed contract or other files
                </button>
                <p className="text-[10px] text-[#C4C4C4] mt-1">PDF, DOC, JPG accepted. Max 8 MB per file.</p>
              </div>

              {sendError && (
                <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600 font-medium">{sendError}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-[#E8E6E2]/60 bg-[#FAFAF8]/50 flex items-center justify-between">
              <p className="text-xs text-[#A3A3A3]">
                {1 + sendFiles.length} attachment{sendFiles.length > 0 ? 's' : ''}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={closeSendModal}
                  className="px-4 py-2 text-sm font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-white rounded-lg border border-[#E8E6E2] transition-all"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || !sendEmail.trim()}
                  className="px-5 py-2 text-sm font-medium text-white bg-[#1A1A1A] rounded-xl hover:bg-[#2a2a2a] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-sm shadow-black/10"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.25"/><path d="M12 2a10 10 0 019.95 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14.5 1.5l-6 13-2.5-5.5L.5 6.5l14-5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M14.5 1.5L6 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                      Send Invoice
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
